import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { ToastContainer } from 'react-toastify'
import DataTable from '../../components/AppDataTable'
import moment from 'moment'
import clsx from 'clsx'
import { fetchData, formatNumber, getTranslatedValue, toasterrormsg } from '../../../reusable'
import { KTIcon } from '../../../_metronic/helpers'
import { PageTitle } from '../../../_metronic/layout/core'
import { Tr } from '../../../_metronic/i18n/Tr'
import { useLang } from '../../../_metronic/i18n/Metronici18n'
import Loader from '../component/Loader'
import { formatDuration } from '../advertisement/bannerAdvertisement/AddAdvertisement.constants'

type SpendTab = 'BANNER' | 'SEARCH'

const useDebounce = (value: string, delay = 500) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

// Same badge palette the impression report uses for ad statuses
const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'APPROVED':
    case 'COMPLETED':
      return 'badge-success'
    case 'UNDER_REVIEW':
      return 'badge-warning'
    case 'REJECTED':
      return 'badge-danger'
    case 'ACTIVATED':
      return 'badge-primary'
    case 'PAUSED':
      return 'badge-info'
    default:
      return 'badge-secondary'
  }
}

const SpendReport: FC = () => {
  const t = Tr()
  const lang = useLang()
  var vendorId = ''
  const categoryId = sessionStorage.getItem('categoryId')
  const vendorData = sessionStorage.getItem('vendor')
  if (vendorData) {
    var vendor = JSON.parse(vendorData)
    vendorId = vendor.vendorId
  }

  const [activeTab, setActiveTab] = useState<SpendTab>('BANNER')
  const [reviewList, setReviewList] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [focus, setFocus] = useState('')
  const [limit, setLimit] = useState(10)
  const [offset, setOffset] = useState(0)
  const [totalRecords, setTotalRecords] = useState('') as any
  const [sortColumn, setSortColumn] = useState('')
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc')
  const [selectedYear, setSelectedYear] = useState('')
  const [selectedMonth, setSelectedMonth] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const debouncedSearch = useDebounce(searchTerm)

  // Both tabs share the same year/month period filter, translated into a
  // start/end date range the APIs understand
  const applyPeriodFilter = (param: any) => {
    if (!selectedYear) return param

    const filterStartDate = selectedMonth
      ? moment(`${selectedYear}-${selectedMonth}-01`).startOf('month')
      : moment(`${selectedYear}-01-01`).startOf('year')
    const filterEndDate = selectedMonth
      ? filterStartDate.clone().endOf('month')
      : filterStartDate.clone().endOf('year')

    param.startDate = filterStartDate.format('YYYY-MM-DD')
    param.endDate = filterEndDate.format('YYYY-MM-DD')

    return param
  }

  // Banner spends come from the banner ads list
  const bannerSpendApiCall = async (term: string) => {
    var param: any = applyPeriodFilter({
      limit,
      offset,
      search: term.replace(/'/g, "''"),
      vendorId,
    })

    var res = await fetchData('public/getvendorads', param)

    if (res.status == '200') {
      var data = res.data
      setTotalRecords(data.totalRecords ?? 0)
      setReviewList(data.data ?? [])
    } else {
      toasterrormsg(res.message)
    }
  }

  // Search spends come from the vendor's sponsored (search) ads list
  const searchSpendApiCall = async (term: string) => {
    var param: any = applyPeriodFilter({
      vendorId: String(vendorId),
      limit,
      offset,
      search: term.replace(/'/g, "''"),
    })

    var res = await fetchData('public/getvendorsponserlist', param)

    if (res.status == '200') {
      var data = res?.data?.data ?? []
      setTotalRecords(Number(res?.data?.totalRecords ?? data.length ?? 0))
      setReviewList(data)
    } else {
      toasterrormsg(res.message)
    }
  }

  const spendListApiCall = async (term: string) => {
    setIsLoading(true)
    if (activeTab === 'SEARCH') {
      await searchSpendApiCall(term)
    } else {
      await bannerSpendApiCall(term)
    }
    setIsLoading(false)
  }

  // Reset paging/filters whenever the vendor switches tabs so the two
  // reports never inherit each other's search or period filter
  const handleTabChange = (tab: SpendTab) => {
    if (tab === activeTab) return
    setActiveTab(tab)
    setReviewList([])
    setTotalRecords(0)
    setSearchTerm('')
    setSelectedYear('')
    setSelectedMonth('')
    setOffset(0)
  }

  useEffect(() => {
    document.title = 'Insayr | View Spend Report'
  }, [])

  const searchData = (text: any, isfoc: any) => {
    setSearchTerm(text)
    setFocus(isfoc)
    setOffset(0)
  }

  const handleSort = (column: any, sortDirection: 'asc' | 'desc') => {
    setSortColumn(column.sortField)
    setSortDirection(sortDirection)
  }

  const CustomHeader = (props: any) => {
    const inputRef: any = useRef(null)

    const handleChange = async (e: any) => {
      await props.fnserch(e, 1)
    }

    useEffect(() => {
      if (inputRef.current && props.isfoc === 1) {
        inputRef.current.focus()
      }
    }, [props.isfoc])

    return (
      <div className='d-flex justify-content-between flex-wrap w-100 my-5 gap-3'>
        <div className='d-flex align-items-center position-relative my-1'>
          <KTIcon iconName='magnifier' className='fs-1 position-absolute ms-6' />
          <input
            type='text'
            data-kt-user-table-filter='search'
            className='form-control form-control-solid w-250px ps-14'
            placeholder={t('Search Spend')}
            value={props.search}
            onChange={(e: any) => handleChange(e.target.value)}
            ref={inputRef}
          />
        </div>
        <div className='d-flex align-items-center gap-3 flex-wrap'>
          <div className='d-flex align-items-center gap-2'>
            <label className='fw-semibold text-muted fs-7 mb-0'>{t('Year')}</label>
            <select
              className='form-select form-select-solid w-120px'
              value={selectedYear}
              onChange={(e) => {
                setSelectedYear(e.target.value)
                setOffset(0)
              }}
            >
              <option value=''>{t('All Years')}</option>
              {Array.from({length: 6}, (_, index) => new Date().getFullYear() - index).map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          <div className='d-flex align-items-center gap-2'>
            <label className='fw-semibold text-muted fs-7 mb-0'>{t('Month')}</label>
            <select
              className='form-select form-select-solid w-160px'
              value={selectedMonth}
              disabled={!selectedYear}
              onChange={(e) => {
                setSelectedMonth(e.target.value)
                setOffset(0)
              }}
            >
              <option value=''>{t('All Months')}</option>
              {[
                'January',
                'February',
                'March',
                'April',
                'May',
                'June',
                'July',
                'August',
                'September',
                'October',
                'November',
                'December',
              ].map((month, index) => (
                <option key={month} value={index + 1}>
                  {t(month)}
                </option>
              ))}
            </select>
          </div>
          {(selectedYear || selectedMonth) && (
            <button
              className='btn btn-sm btn-light-danger'
              onClick={() => {
                setSelectedYear('')
                setSelectedMonth('')
                setOffset(0)
              }}
            >
              {t('Clear')}
            </button>
          )}
        </div>
      </div>
    )
  }

  // Column order follows the agreed report structure:
  // Banner Title | Status | Payment Status | View Count | Duration |
  // Total Price | Start Date | End Date | Transaction Number
  const bannerColumns = useMemo(
    () => [
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Banner Title')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {getTranslatedValue(lang, row, '_name') || row.en_name || row.ru_name || row.uz_name || '-'}
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'en_name',
        width: '200px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Status')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            {row.status ? (
              <span className={`badge mx-2 ${statusBadgeClass(row.status)}`}>{t(row.status)}</span>
            ) : (
              <span className='badge badge-secondary mx-2'>-</span>
            )}
          </div>
        ),
        sortable: true,
        sortField: 'status',
        width: '160px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Payment Status')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            {row.paymentStatus === 'PENDING' ? (
              <span className='badge badge-warning mx-2'>{t('PENDING')}</span>
            ) : row.paymentStatus === 'PAID' ? (
              <span className='badge badge-success mx-2'>{t('PAID')}</span>
            ) : row.paymentStatus === 'REJECTED' ? (
              <span className='badge badge-danger mx-2'>{t('REJECTED')}</span>
            ) : (
              <span>-</span>
            )}
          </div>
        ),
        sortable: true,
        sortField: 'paymentStatus',
        width: '170px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('View Count')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.viewCount ?? '-'}</div>
          </div>
        ),
        sortable: true,
        sortField: 'viewCount',
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Duration')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {formatDuration(row?.duration, row?.durationType, lang)}
            </div>
          </div>
        ),
        sortable: false,
        width: '140px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('Total Price')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.price != null ? `${formatNumber(row.price)} UZS` : '-'}</div>
          </div>
        ),
        sortable: true,
        sortField: 'price',
        width: '150px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('Start Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.startDate ? moment(row.startDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'startDate',
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('End Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.endDate ? moment(row.endDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'endDate',
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Transaction Number')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.transactionNumber || '-'}</div>
          </div>
        ),
        sortable: true,
        sortField: 'transactionNumber',
        width: '190px',
      },
    ],
    [categoryId, lang, t]
  )

  // Search (sponsored) spends: Property Name | Status | Payment Status | Total Price |
  // View Count | Clicks | Duration | Start Date | End Date | Transaction Number
  const searchColumns = useMemo(
    () => [
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Property Name')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {getTranslatedValue(lang, row, '_name') || row.en_name || row.ru_name || row.uz_name || '-'}
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'en_name',
        width: '200px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Status')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            {row.status ? (
              <span className={`badge mx-2 ${statusBadgeClass(row.status)}`}>{t(row.status)}</span>
            ) : (
              <span className='badge badge-secondary mx-2'>-</span>
            )}
          </div>
        ),
        sortable: true,
        sortField: 'status',
        width: '160px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Payment Status')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            {row.paymentStatus === 'PENDING' ? (
              <span className='badge badge-warning mx-2'>{t('PENDING')}</span>
            ) : row.paymentStatus === 'PAID' ? (
              <span className='badge badge-success mx-2'>{t('PAID')}</span>
            ) : row.paymentStatus === 'REJECTED' ? (
              <span className='badge badge-danger mx-2'>{t('REJECTED')}</span>
            ) : (
              <span>-</span>
            )}
          </div>
        ),
        sortable: true,
        sortField: 'paymentStatus',
        width: '170px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('Total Price')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.price != null ? `${formatNumber(row.price)} UZS` : '-'}</div>
          </div>
        ),
        sortable: true,
        sortField: 'price',
        width: '150px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('View Count')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {(row.impressionCount) != null
                ? formatNumber(Number(row.impressionCount))
                : '-'}
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'viewCount',
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('Clicks')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {(row.viewCount) != null
                ? formatNumber(Number(row.viewCount))
                : '-'}
            </div>
          </div>
        ),
        sortable: false,
        width: '120px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Duration')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {formatDuration(row?.duration, row?.durationType, lang)}
            </div>
          </div>
        ),
        sortable: false,
        width: '140px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('Start Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.startDate ? moment(row.startDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'startDate',
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted text-nowrap'>{t('End Date')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>
              {row.endDate ? moment(row.endDate).format('DD.MM.YYYY') : '-'}
            </div>
          </div>
        ),
        sortable: true,
        sortField: 'endDate',
        width: '130px',
      },
      {
        name: <label className='fw-bold fs-6 text-muted'>{t('Transaction Number')}</label>,
        cell: (row: any) => (
          <div className='my-3'>
            <div className='fw-bold fs-5'>{row.transactionNumber || '-'}</div>
          </div>
        ),
        sortable: true,
        sortField: 'transactionNumber',
        width: '190px',
      },
    ],
    [lang, t]
  )

  useEffect(() => {
    spendListApiCall(debouncedSearch)
  }, [
    activeTab,
    limit,
    offset,
    sortColumn,
    sortDirection,
    debouncedSearch,
    selectedYear,
    selectedMonth,
    categoryId,
  ])

  const handlePageChange = (newPage: number) => {
    const totalPages = Math.ceil(totalRecords / limit)
    if (newPage === 1) {
      setOffset(0)
    } else if (newPage === totalPages) {
      setOffset((totalPages - 1) * limit)
    } else {
      setOffset((newPage - 1) * limit)
    }
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setLimit(newRowsPerPage)
  }

  const tabs: { key: SpendTab; label: string }[] = [
    { key: 'BANNER', label: 'Banner Spend' },
    { key: 'SEARCH', label: 'Search Spend' },
  ]

  return (
    <>
      {isLoading && <Loader />}
      <ToastContainer />
      <PageTitle breadcrumbs={[]}>{t('View Spend Report')}</PageTitle>
      <div className='card card-flush py-4'>
        <div className='card-body pt-0'>
          <div className='card card-custom'>
            <div className='card-header card-header-stretch overflow-auto'>
              <ul
                className='nav nav-stretch nav-line-tabs fw-semibold border-transparent flex-nowrap'
                role='tablist'
              >
                {tabs.map((tab) => (
                  <li className='nav-item' key={tab.key}>
                    <a
                      className={clsx('nav-link cursor-pointer', {
                        active: activeTab === tab.key,
                      })}
                      onClick={() => handleTabChange(tab.key)}
                      role='tab'
                    >
                      {t(tab.label)}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <DataTable
            // Remount per tab so the paginator's internal page resets with the offset
            key={activeTab}
            columns={activeTab === 'SEARCH' ? searchColumns : bannerColumns}
            data={reviewList}
            pagination
            paginationServer={true}
            onChangePage={handlePageChange}
            paginationTotalRows={totalRecords}
            paginationPerPage={limit}
            onChangeRowsPerPage={handleRowsPerPageChange}
            paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 30]}
            subHeader={true}
            subHeaderComponent={
              <CustomHeader
                search={searchTerm}
                isfoc={focus}
                fnserch={searchData}
              />
            }
            onSort={handleSort}
            paginationComponentOptions={{ rowsPerPageText: t('Rows Per Page') }}
            sortServer
          />
        </div>
      </div>
    </>
  )
}

export default SpendReport
